import { AdminWithdrawals } from './AdminWithdrawals';
import { AdminTermsHistory } from './AdminTermsHistory';
import { AdminReports } from './AdminReports';
import { AdminPushNotifications } from './AdminPushNotifications';
import { AdminInAppNotifications } from './AdminInAppNotifications';
import { AdminEmployerVerification } from './AdminEmployerVerification';
import { AdminDiscountCodes } from './AdminDiscountCodes';
import { AdminInactiveAccounts } from './AdminInactiveAccounts';
import { AdminHealthDashboard } from './AdminHealthDashboard'; // ✅ Health monitoring
import { AdminAnnouncements } from './AdminAnnouncements'; // ✅ Announcement modals
import { AdminWelcomeModals } from './AdminWelcomeModals'; // ✅ Welcome modals
import { AdminEmailLists } from './AdminEmailLists'; // ✅ Email lists for GDPR
import { AdminBulkEmailSender } from './AdminBulkEmailSender'; // ✅ Bulk email sender
import { AdminAccessControl } from './AdminAccessControl'; // ✅ Sub-admin system
import { AdminGigs } from './AdminGigs'; // ✅ FASE 6: Gigs management
import { LogViewer } from './LogViewer'; // ✅ Dev log viewer
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { projectId, API_BASE } from '../utils/supabase/info';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, Crown, ImageIcon, MessageSquare, TrendingUp, DollarSign, Trash2, Shield, FileText, BarChart3, MapPin, Trophy, Search, AlertTriangle, CheckCircle, XCircle, Eye, Download, RefreshCw, Filter, Bell, Send, Briefcase, UserCheck, X, ExternalLink } from 'lucide-react';
import { Loader } from './ui/Loader';
import { formatDistanceToNow } from '../utils/date-utils';
import { toast } from '../utils/toast';
import { ConfirmDialog } from './ConfirmDialog';
import { AdminModalZContext } from './AdminModalZContext';
import { devError } from '../utils/logger';
import { isPremiumSubscription } from '../utils/accessPolicy';
import { useNav } from '../contexts/NavContext';

const ADMIN_MODAL_Z = 10001; // Body-portaled modals above admin overlay (var(--z-admin-modal))


interface AdminStats {
  totalUsers: number;
  totalPremiumUsers: number;
  totalPosts: number;
  totalMessages: number;
  revenue: number;
  newUsersToday: number;
  newPostsToday: number;
  talentUsers: number;
  employerTypeUsers: number;
  dailyRegistrations: Array<{ date: string; count: number }>;
  topSkills: Array<{ skill: string; count: number }>;
  topLocations: Array<{ location: string; count: number }>;
  totalVisits?: number;
  visitsToday?: number;
  dailyVisits?: Array<{ date: string; count: number }>;
  visitSources?: Array<{ source: string; count: number }>;
  signupFunnel?: {
    last30Days: {
      landingView: number;
      openAuth: number;
      startSignup: number;
      signupSuccess: number;
      emailVerified: number;
    };
    today: {
      landingView: number;
      openAuth: number;
      startSignup: number;
      signupSuccess: number;
      emailVerified: number;
    };
    rates: {
      visitToAuthOpen: number;
      authOpenToSignupStart: number;
      signupStartToSuccess: number;
      signupSuccessToEmailVerified: number;
    };
    daily: Array<{
      date: string;
      landingView: number;
      openAuth: number;
      startSignup: number;
      signupSuccess: number;
      emailVerified: number;
    }>;
  };
}

interface User {
  id: string;
  email: string;
  createdAt: string; // ✅ Changed to camelCase
  lastActive: string; // ✅ Changed to camelCase
  isPremium: boolean; // ✅ Changed to camelCase
  subscription: 'free' | 'plus' | 'employer' | 'employer_plus';
  userType: string; // ✅ Changed to camelCase
  portfolioCount: number;
  subscriptionPurchasedAt?: string; // ✅ Changed to camelCase
  metadata?: {
    fullName?: string; // ✅ Changed to camelCase
    county?: string;
    categories?: string[];
  };
}

interface RevenueStats {
  monthlyRecurring: number;
  employerRevenue: number;
  employerPlusRevenue: number;
  plusRevenue: number;
  activeEmployerCount: number;
  activeEmployerPlusCount: number;
  activePlusCount: number;
  breakdown: {
    employer: { count: number; revenue: number };
    employerPlus: { count: number; revenue: number };
    plus: { count: number; revenue: number };
  };
}

function getAdminSubscriptionBadgeClass(subscription: User['subscription']): string {
  if (subscription === 'employer') return 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-900';
  if (subscription === 'employer_plus') return 'bg-gradient-to-r from-purple-100 via-pink-100 to-orange-100 text-purple-900';
  return 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-900';
}

function getAdminPremiumStatusLabel(subscription: User['subscription']): string {
  if (subscription === 'employer') return 'Basis';
  if (subscription === 'employer_plus') return 'Arbeidsgiver Pluss';
  return 'Talentfunn Pluss';
}

export function AdminDashboard({ accessToken, onBack }: { accessToken: string; onBack: () => void }) {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'visits' | 'funnel' | 'users' | 'revenue' | 'employers' | 'reports' | 'terms' | 'push' | 'inapp' | 'announcements' | 'welcome' | 'discounts' | 'deletion' | 'angreretter' | 'health' | 'costs' | 'scenario' | 'emails' | 'bulkemail' | 'access' | 'logs' | 'gigs' | 'topposts'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null);
  const [userTypeFilter, setUserTypeFilter] = useState<string>('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [topPosts, setTopPosts] = useState<Array<{ id: string; title: string; views: number; userId: string; userName: string }>>([]);
  const [loadingTopPosts, setLoadingTopPosts] = useState(false);
  const { navigateTo } = useNav();
  
  // ✅ NEW: Cost & MVA states
  const [adminSettings, setAdminSettings] = useState<any>(null);
  const [costStats, setCostStats] = useState<any>(null);
  
  // ✅ NEW: Scenario calculator states
  const [scenarioInputs, setScenarioInputs] = useState({
    newUsersPerMonth: 100,
    talentConversionRate: 10, // %
    employerConversionRate: 5, // %
    monthlyChurn: 5, // %
    talentPlussPrice: 149,
    employerMessagingPrice: 490,
    employerPlussPrice: 990,
  });
  
  // Confirm dialog states
  const [showSubscriptionConfirm, setShowSubscriptionConfirm] = useState(false);
  const [userToToggle, setUserToToggle] = useState<string | null>(null);
  const [togglingSubscription, setTogglingSubscription] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);

  // User content modal states
  const [showUserContentModal, setShowUserContentModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userPosts, setUserPosts] = useState<any[]>([]);
  const [loadingUserContent, setLoadingUserContent] = useState(false);
  
  // Send notification to single user states
  const [showNotificationForm, setShowNotificationForm] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('admin_announcement');
  const [notificationDestination, setNotificationDestination] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  
  // ✅ NEW: Log viewer state
  const [showLogViewer, setShowLogViewer] = useState(false);
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchData();
  }, []);

  // ✅ OPTIMIZED: Auto-refresh Users tab every 60 seconds (reduced from 10s to prevent overload)
  useEffect(() => {
    if (activeTab !== 'users') return;

    const interval = setInterval(() => {
      fetchUsers(); // Only refresh users, not stats
    }, 60000); // 60 seconds (CRITICAL FIX: reduced from 10s to prevent database/network overload)

    return () => clearInterval(interval);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'topposts') fetchTopPosts();
  }, [activeTab]);

  const fetchData = async () => {
    await Promise.all([
      fetchStats(),
      fetchUsers(),
      fetchRevenueStats(),
      fetchAdminSettings(),
      fetchCostStats()
    ]);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchData(),
      activeTab === 'topposts' ? fetchTopPosts() : Promise.resolve(),
    ]);
    setRefreshing(false);
    toast.success('Data oppdatert!');
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/stats`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 403) {
        toast.error('Du har ikke tilgang til admin-dashboard');
        onBack();
      } else {
        const errorText = await response.text();
        devError('Admin stats error:', response.status, errorText);
        toast.error(`Feil ved lasting av admin-data: ${response.status}`);
      }
    } catch (error) {
      devError('Error fetching admin stats:', error);
      toast.error(`Feil ved lasting av admin-data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/users`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      devError('Error fetching users:', error);
    }
  };

  const fetchTopPosts = async () => {
    setLoadingTopPosts(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/top-posts`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTopPosts(data.posts || []);
      } else {
        toast.error('Kunne ikke laste mest sette prosjekter');
      }
    } catch (error) {
      devError('Error fetching top posts:', error);
      toast.error('Kunne ikke laste mest sette prosjekter');
    } finally {
      setLoadingTopPosts(false);
    }
  };

  const handleToggleSubscriptionClick = (userId: string) => {
    setUserToToggle(userId);
    setShowSubscriptionConfirm(true);
  };

  const toggleSubscription = async () => {
    if (!userToToggle) return;
    
    setTogglingSubscription(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/toggle-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ user_id: userToToggle }),
        }
      );

      if (response.ok) {
        toast.success('Abonnement endret!');
        await fetchUsers();
        await fetchStats();
      }
    } catch (error) {
      devError('Error toggling subscription:', error);
      toast.error('Feil ved endring av abonnement');
    } finally {
      setTogglingSubscription(false);
      setShowSubscriptionConfirm(false);
      setUserToToggle(null);
    }
  };

  const handleDeleteUserClick = (userId: string) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/user/${userToDelete}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Bruker slettet!');
        await fetchUsers();
        await fetchStats();
      } else {
        const errorData = await response.json();
        toast.error(`Kunne ikke slette bruker: ${errorData.error || 'Ukjent feil'}`);
      }
    } catch (error) {
      devError('Error deleting user:', error);
      toast.error(`Feil ved sletting av bruker: ${error.message}`);
    } finally {
      setDeletingUser(false);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const fetchRevenueStats = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/revenue-stats`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRevenueStats(data);
      }
    } catch (error) {
      devError('Error fetching revenue stats:', error);
    }
  };

  // ✅ NEW: Fetch admin settings
  const fetchAdminSettings = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/settings`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAdminSettings(data);
      }
    } catch (error) {
      devError('Error fetching admin settings:', error);
    }
  };

  // ✅ NEW: Fetch cost stats
  const fetchCostStats = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/cost-stats`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCostStats(data);
      }
    } catch (error) {
      devError('Error fetching cost stats:', error);
    }
  };

  const exportStats = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/export-stats`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `talentfunn-stats-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Statistikk eksportert!');
      }
    } catch (error) {
      devError('Error exporting stats:', error);
      toast.error('Feil ved eksport av statistikk');
    }
  };

  const fetchUserContent = async (userId: string) => {
    setLoadingUserContent(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/user-content/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserPosts(data.posts);
      }
    } catch (error) {
      devError('Error fetching user content:', error);
      toast.error('Feil ved lasting av brukerinnhold');
    } finally {
      setLoadingUserContent(false);
    }
  };

  const sendNotificationToUser = async () => {
    if (!selectedUserId || !notificationTitle.trim() || !notificationMessage.trim()) {
      toast.error('Tittel og melding er påkrevd');
      return;
    }

    setSendingNotification(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/send-in-app-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            user_id: selectedUserId,
            title: notificationTitle,
            message: notificationMessage,
            type: notificationType,
            destination: notificationDestination || null
          })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Varsel sendt til brukeren!');
        // Reset form
        setNotificationTitle('');
        setNotificationMessage('');
        setNotificationType('admin_announcement');
        setNotificationDestination('');
        setShowNotificationForm(false);
      } else {
        toast.error(`Feil: ${data.error}`);
      }
    } catch (error) {
      devError('Error sending notification:', error);
      toast.error('Noe gikk galt ved sending av varsel');
    } finally {
      setSendingNotification(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="text-center">
          <Loader size="lg" className="text-purple-600" />
          <p className="mt-4 text-purple-900">Laster admin-data...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 admin-area">
        <div className="text-center">
          <p className="text-purple-900">Kunne ikke laste admin-data</p>
        </div>
      </div>
    );
  }

  const COLORS = ['#9333ea', '#ec4899', '#f97316', '#84cc16', '#06b6d4', '#6366f1'];

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.metadata?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesUserType = userTypeFilter === 'all' || user.userType === userTypeFilter;
    const matchesSubscription = subscriptionFilter === 'all' || user.subscription === subscriptionFilter;
    return matchesSearch && matchesUserType && matchesSubscription;
  });

  // Portal to document.body so admin UI sits on top of app layout and reliably receives clicks (no overlay/stacking issues)
  return createPortal(
    <div className="fixed inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 admin-area flex flex-col overflow-hidden" style={{ zIndex: 'var(--z-admin-overlay)' }}>
      <AdminModalZContext.Provider value={ADMIN_MODAL_Z}>
      {/* Sticky Admin Header with Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-purple-100 flex-shrink-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 xl:px-8">
          <div className="flex justify-between items-center h-16 gap-4">
            {/* Logo and Admin Title */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <Shield className="w-6 h-6 text-purple-600" />
              <span className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent font-semibold pb-1 whitespace-nowrap">
                Admin Dashboard
              </span>
            </div>
            
            {/* Admin Navigation - Desktop Select Dropdown */}
            <div className="hidden md:block flex-shrink-0">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as any)}
                className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer bg-white shadow-sm"
              >
                <option value="overview">Oversikt</option>
                <option value="visits">Besøk</option>
                <option value="funnel">Funnel</option>
                <option value="users">Brukere</option>
                <option value="revenue">Inntekt</option>
                <option value="employers">Arbeidsgivere</option>
                <option value="gigs">Oppdrag</option>
                <option value="topposts">Mest sette prosjekter</option>
                <option value="emails">E-postlister</option>
                <option value="bulkemail">Bulk E-post</option>
                <option value="access">Tilgangsstyring</option>
                <option value="reports">Rapporter</option>
                <option value="terms">Vilkårshistorikk</option>
                <option value="push">Push-varsler</option>
                <option value="inapp">In-app-varsler</option>
                <option value="announcements">Announcements</option>
                <option value="welcome">Welcome Modals</option>
                <option value="discounts">Rabatter</option>
                <option value="deletion">Inaktive kontoer</option>
                <option value="angreretter">Angreretter</option>
                <option value="health">System Health</option>
                <option value="costs">Utgifter & Stats</option>
                <option value="scenario">Leking med Tall</option>
                <option value="logs">Dev Logger</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-3 py-2 text-purple-600 hover:bg-purple-50 rounded-full transition-all"
                title="Oppdater data"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => {
                  setShowLogViewer(false);
                  onBack();
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-purple-50 rounded-full transition-all text-base whitespace-nowrap"
              >
                Tilbake
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile tabs - Native Select */}
        <div className="md:hidden px-4 pb-3">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as any)}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer bg-white shadow-sm"
          >
            <option value="overview">Oversikt</option>
            <option value="visits">Besøk</option>
            <option value="funnel">Funnel</option>
            <option value="users">Brukere</option>
            <option value="revenue">Inntekt</option>
            <option value="employers">Arbeidsgivere</option>
            <option value="gigs">Oppdrag</option>
            <option value="topposts">Mest sette prosjekter</option>
            <option value="emails">E-postlister</option>
            <option value="bulkemail">Bulk E-post</option>
            <option value="access">Tilgangsstyring</option>
            <option value="reports">Rapporter</option>
            <option value="terms">Vilkårshistorikk</option>
            <option value="push">Push-varsler</option>
            <option value="inapp">In-app-varsler</option>
            <option value="announcements">Announcements</option>
            <option value="discounts">Rabatter</option>
            <option value="deletion">Inaktive kontoer</option>
            <option value="angreretter">Angreretter</option>
            <option value="health">System Health</option>
            <option value="costs">Utgifter & Stats</option>
            <option value="scenario">Leking med Tall</option>
            <option value="logs">Dev Logger</option>
          </select>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 pt-6 pb-24 sm:pb-12">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="flex justify-end">
                <button
                  onClick={exportStats}
                  className="px-4 py-2 bg-white text-purple-600 rounded-full hover:bg-purple-50 transition-all flex items-center gap-2 shadow-md border border-purple-100"
                >
                  <Download className="w-4 h-4" />
                  Eksporter statistikk (GDPR-safe)
                </button>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    <span className="text-gray-600">Totalt brukere</span>
                  </div>
                  <p className="text-3xl text-purple-900">{stats.totalUsers}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {stats.newUsersToday} nye i dag
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Crown className="w-5 h-5 text-pink-600" />
                    <span className="text-gray-600">Premium-brukere</span>
                  </div>
                  <p className="text-3xl text-pink-900">{stats.totalPremiumUsers}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {stats.totalUsers - stats.totalPremiumUsers} gratis
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-orange-100">
                  <div className="flex items-center gap-3 mb-2">
                    <ImageIcon className="w-5 h-5 text-orange-600" />
                    <span className="text-gray-600">Portfolioer</span>
                  </div>
                  <p className="text-3xl text-orange-900">{stats.totalPosts}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {stats.newPostsToday} nye i dag
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-green-100">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <span className="text-gray-600">Månedlig inntekt</span>
                  </div>
                  <p className="text-3xl text-green-900">{stats.revenue} kr</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {stats.totalPremiumUsers} betalende
                  </p>
                </div>
              </div>

              {/* User Type Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-blue-100">
                  <div className="flex items-center gap-3 mb-2">
                    <UserCheck className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-600">Talent-brukere</span>
                  </div>
                  <p className="text-3xl text-blue-900">{stats.talentUsers}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {Math.round((stats.talentUsers / stats.totalUsers) * 100)}% av totalt
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-cyan-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Briefcase className="w-5 h-5 text-cyan-600" />
                    <span className="text-gray-600">Arbeidsgiver-brukere</span>
                  </div>
                  <p className="text-3xl text-cyan-900">{stats.employerTypeUsers}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {Math.round((stats.employerTypeUsers / stats.totalUsers) * 100)}% av totalt
                  </p>
                </div>
              </div>

              {/* Daily Registrations Chart */}
              {stats.dailyRegistrations && stats.dailyRegistrations.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                  <h2 className="text-gray-800 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    Nye brukere siste 30 dager
                  </h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.dailyRegistrations}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => new Date(value).toLocaleDateString('nb-NO', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        labelFormatter={(value) => new Date(value).toLocaleDateString('nb-NO')}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Bar dataKey="count" fill="url(#colorGradient)" radius={[8, 8, 0, 0]} />
                      <defs>
                        <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#9333ea" />
                          <stop offset="100%" stopColor="#ec4899" />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Skills and Locations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top Skills */}
                {stats.topSkills && stats.topSkills.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                    <h2 className="text-gray-800 mb-4 flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-purple-600" />
                      Mest populære ferdigheter
                    </h2>
                    <div className="space-y-3">
                      {stats.topSkills.slice(0, 8).map((cat, index) => (
                        <div key={cat.skill} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-gray-400 w-6">{index + 1}.</span>
                            <span className="text-gray-800">{cat.skill}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="h-2 bg-purple-100 rounded-full overflow-hidden" style={{ width: '100px' }}>
                              <div
                                className="h-full bg-gradient-to-r from-purple-600 to-pink-600"
                                style={{ width: `${(cat.count / stats.topSkills[0].count) * 100}%` }}
                              />
                            </div>
                            <span className="text-gray-600 w-8 text-right">{cat.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Locations */}
                {stats.topLocations && stats.topLocations.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                    <h2 className="text-gray-800 mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-purple-600" />
                      Brukere per lokasjon
                    </h2>
                    <div className="space-y-3">
                      {stats.topLocations.slice(0, 8).map((loc, index) => (
                        <div key={loc.location} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-gray-400 w-6">{index + 1}.</span>
                            <span className="text-gray-800">{loc.location}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="h-2 bg-pink-100 rounded-full overflow-hidden" style={{ width: '100px' }}>
                              <div
                                className="h-full bg-gradient-to-r from-pink-600 to-orange-600"
                                style={{ width: `${(loc.count / stats.topLocations[0].count) * 100}%` }}
                              />
                            </div>
                            <span className="text-gray-600 w-8 text-right">{loc.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Empty state messages */}
              {(!stats.topSkills || stats.topSkills.length === 0) && (
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center text-gray-500">
                  Ingen kategoridata tilgjengelig ennå
                </div>
              )}
            </div>
          )}

          {/* Visits / Traffic Tab */}
          {activeTab === 'visits' && (
            <div className="space-y-6">
              <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Eye className="w-6 h-6 text-indigo-600" />
                Trafikk og besøk
              </h1>
              <p className="text-gray-600 text-sm">
                Unike besøk telles én gang per sesjon per enhet (rate-limited på backend). Tall er i UTC-dag.
              </p>

              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                  <span className="text-gray-600 text-sm">Totalt besøk</span>
                  <p className="text-3xl font-semibold text-indigo-900 mt-1">{stats?.totalVisits ?? 0}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                  <span className="text-gray-600 text-sm">Besøk i dag</span>
                  <p className="text-3xl font-semibold text-indigo-900 mt-1">{stats?.visitsToday ?? 0}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                  <span className="text-gray-600 text-sm">Snitt siste 7 dager</span>
                  <p className="text-3xl font-semibold text-indigo-900 mt-1">
                    {stats?.dailyVisits?.length
                      ? Math.round(
                          stats.dailyVisits.slice(-7).reduce((a, d) => a + d.count, 0) / 7
                        )
                      : 0}
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                  <span className="text-gray-600 text-sm">Snitt siste 30 dager</span>
                  <p className="text-3xl font-semibold text-indigo-900 mt-1">
                    {stats?.dailyVisits?.length
                      ? Math.round(
                          stats.dailyVisits.reduce((a, d) => a + d.count, 0) / stats.dailyVisits.length
                        )
                      : 0}
                  </p>
                </div>
              </div>

              {/* Daily visits chart */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                <h2 className="text-gray-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Besøk per dag (siste 30 dager)
                </h2>
                {stats?.dailyVisits && stats.dailyVisits.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={stats.dailyVisits}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => new Date(value + 'Z').toLocaleDateString('nb-NO', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        labelFormatter={(value) => new Date(value + 'Z').toLocaleDateString('nb-NO')}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Bar dataKey="count" fill="#4f46e5" radius={[8, 8, 0, 0]} name="Besøk" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[320px] flex items-center justify-center text-gray-500">
                    Ingen besøksdata ennå. Trafikk vises når brukere har åpnet appen.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Funnel Tab */}
          {activeTab === 'funnel' && (
            <div className="space-y-6">
              <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
                Registrerings-funnel
              </h1>
              <p className="text-gray-600 text-sm">
                Viser hvordan besøk går gjennom stegene: besøk → åpnet auth → startet registrering → konto opprettet → e-post bekreftet.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                  <span className="text-gray-600 text-sm">Besøk (30 dager)</span>
                  <p className="text-3xl font-semibold text-indigo-900 mt-1">{stats?.signupFunnel?.last30Days.landingView ?? 0}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                  <span className="text-gray-600 text-sm">Åpnet auth</span>
                  <p className="text-3xl font-semibold text-indigo-900 mt-1">{stats?.signupFunnel?.last30Days.openAuth ?? 0}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                  <span className="text-gray-600 text-sm">Startet registrering</span>
                  <p className="text-3xl font-semibold text-indigo-900 mt-1">{stats?.signupFunnel?.last30Days.startSignup ?? 0}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                  <span className="text-gray-600 text-sm">Konto opprettet</span>
                  <p className="text-3xl font-semibold text-indigo-900 mt-1">{stats?.signupFunnel?.last30Days.signupSuccess ?? 0}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                  <span className="text-gray-600 text-sm">E-post bekreftet</span>
                  <p className="text-3xl font-semibold text-indigo-900 mt-1">{stats?.signupFunnel?.last30Days.emailVerified ?? 0}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                  <span className="text-gray-600 text-sm">Besøk → auth åpnet</span>
                  <p className="text-3xl font-semibold text-purple-900 mt-1">{stats?.signupFunnel?.rates.visitToAuthOpen ?? 0}%</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                  <span className="text-gray-600 text-sm">Auth åpnet → start</span>
                  <p className="text-3xl font-semibold text-purple-900 mt-1">{stats?.signupFunnel?.rates.authOpenToSignupStart ?? 0}%</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                  <span className="text-gray-600 text-sm">Start → konto opprettet</span>
                  <p className="text-3xl font-semibold text-purple-900 mt-1">{stats?.signupFunnel?.rates.signupStartToSuccess ?? 0}%</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                  <span className="text-gray-600 text-sm">Konto opprettet → e-post bekreftet</span>
                  <p className="text-3xl font-semibold text-purple-900 mt-1">{stats?.signupFunnel?.rates.signupSuccessToEmailVerified ?? 0}%</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-indigo-100">
                <h2 className="text-gray-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Funnel per dag (siste 30 dager)
                </h2>
                {stats?.signupFunnel?.daily && stats.signupFunnel.daily.length > 0 ? (
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={stats.signupFunnel.daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => new Date(value + 'Z').toLocaleDateString('nb-NO', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        labelFormatter={(value) => new Date(value + 'Z').toLocaleDateString('nb-NO')}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Bar dataKey="landingView" fill="#6366f1" name="Besøk" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="openAuth" fill="#8b5cf6" name="Åpnet auth" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="startSignup" fill="#ec4899" name="Startet registrering" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="signupSuccess" fill="#10b981" name="Konto opprettet" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="emailVerified" fill="#f59e0b" name="E-post bekreftet" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[320px] flex items-center justify-center text-gray-500">
                    Ingen funnel-data ennå.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              {/* Search and Filters */}
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-purple-100">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Søk etter navn eller e-post..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <select
                    value={userTypeFilter}
                    onChange={(e) => setUserTypeFilter(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="all">Alle brukertyper</option>
                    <option value="talent">Talent</option>
                    <option value="employer">Arbeidsgiver</option>
                  </select>
                  <select
                    value={subscriptionFilter}
                    onChange={(e) => setSubscriptionFilter(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="all">Alle abonnementer</option>
                    <option value="free">Gratis</option>
                    <option value="plus">Talentfunn Pluss</option>
                    <option value="employer">Arbeidsgiver Basis</option>
                    <option value="employer_plus">Arbeidsgiver Pluss</option>
                  </select>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Viser {filteredUsers.length} av {users.length} brukere
                </p>
              </div>

              {/* Users Table */}
              <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Bruker</th>
                        <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">E-post</th>
                        <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Type</th>
                        <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Status</th>
                        <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Portfolioer</th>
                        <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Registrert</th>
                        <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Sist aktiv</th>
                        <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Handlinger</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-purple-50 transition-colors">
                          <td className="px-6 py-4">
                            <span className="text-gray-900">{user.metadata?.fullName || 'Ukjent'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-gray-600 text-sm">{user.email}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm capitalize">
                              {user.userType || 'Ukjent'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {isPremiumSubscription(user.subscription) ? (
                                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                                  getAdminSubscriptionBadgeClass(user.subscription)
                                }`}>
                                  <Crown className="w-3 h-3" />
                                  {getAdminPremiumStatusLabel(user.subscription)}
                                </span>
                              ) : (
                                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                                  Gratis
                                </span>
                              )}
                              {/* ✅ Withdrawal Period Expired Icon */}
                              {isPremiumSubscription(user.subscription) && user.subscriptionPurchasedAt && (() => {
                                const purchaseDate = new Date(user.subscriptionPurchasedAt);
                                const fourteenDaysAgo = new Date();
                                fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
                                const isExpired = purchaseDate < fourteenDaysAgo;
                                
                                return isExpired && (
                                  <div
                                    className="flex-shrink-0"
                                    title={`Angrefristen er utgått (kjøpt ${purchaseDate.toLocaleDateString('nb-NO')})`}
                                  >
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-gray-700">{user.portfolioCount}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-gray-600 text-sm">
                              {formatDistanceToNow(user.createdAt)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-gray-600 text-sm">
                              {formatDistanceToNow(user.lastActive)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setSelectedUserId(user.id);
                                  setShowUserContentModal(true);
                                  fetchUserContent(user.id);
                                }}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                                title="Se innhold"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {/* ✅ Only show "Gi Pluss" button for Talent users */}
                              {user.userType === 'talent' && (
                                <button
                                  onClick={() => handleToggleSubscriptionClick(user.id)}
                                  className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
                                  title={isPremiumSubscription(user.subscription) ? 'Fjern Pluss' : 'Gi Pluss'}
                                >
                                  <Crown className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteUserClick(user.id)}
                                className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                                title="Slett bruker"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {filteredUsers.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  Ingen brukere funnet
                </div>
              )}
            </div>
          )}

          {/* Revenue Tab */}
          {activeTab === 'revenue' && revenueStats && (
            <div className="space-y-6">
              {/* Revenue Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                    <span className="text-gray-600">Total inntekt (måned)</span>
                  </div>
                  <p className="text-3xl text-purple-900">{revenueStats.monthlyRecurring} kr</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Totalt aktive abonnementer
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-blue-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-600">Arbeidsgiver Basis</span>
                  </div>
                  <p className="text-3xl text-blue-900">{revenueStats.employerRevenue} kr</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {revenueStats.activeEmployerCount} aktive × 490 kr
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-cyan-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Briefcase className="w-5 h-5 text-cyan-600" />
                    <span className="text-gray-600">Arbeidsgiver Pluss</span>
                  </div>
                  <p className="text-3xl text-cyan-900">{revenueStats.employerPlusRevenue} kr</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {revenueStats.activeEmployerPlusCount} aktive × 990 kr
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Crown className="w-5 h-5 text-pink-600" />
                    <span className="text-gray-600">Talentfunn Pluss</span>
                  </div>
                  <p className="text-3xl text-pink-900">{revenueStats.plusRevenue} kr</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {revenueStats.activePlusCount} aktive × 149 kr
                  </p>
                </div>
              </div>

              {/* Breakdown */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                <h2 className="text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Inntektsfordeling
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                    <div>
                      <p className="text-gray-800 font-medium">Arbeidsgiver Basis</p>
                      <p className="text-sm text-gray-600">490 kr/mnd per bruker</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl text-blue-900">{revenueStats.breakdown.employer.count}</p>
                      <p className="text-sm text-gray-600">{revenueStats.breakdown.employer.revenue} kr</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-cyan-50 rounded-xl">
                    <div>
                      <p className="text-gray-800 font-medium">Arbeidsgiver Pluss</p>
                      <p className="text-sm text-gray-600">990 kr/mnd per bruker</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl text-cyan-900">{revenueStats.breakdown.employerPlus.count}</p>
                      <p className="text-sm text-gray-600">{revenueStats.breakdown.employerPlus.revenue} kr</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-pink-50 rounded-xl">
                    <div>
                      <p className="text-gray-800 font-medium">Talentfunn Pluss</p>
                      <p className="text-sm text-gray-600">149 kr/mnd per bruker</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl text-pink-900">{revenueStats.breakdown.plus.count}</p>
                      <p className="text-sm text-gray-600">{revenueStats.breakdown.plus.revenue} kr</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Projections */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-green-100">
                <h2 className="text-gray-800 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Inntektsprognoser
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-gray-600 mb-1">Årlig inntekt (estimert)</p>
                    <p className="text-2xl text-green-900">{revenueStats.monthlyRecurring * 12} kr</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Gjennomsnittlig per bruker</p>
                    <p className="text-2xl text-green-900">
                      {Math.round(
                        revenueStats.monthlyRecurring /
                        (revenueStats.activeEmployerCount + revenueStats.activeEmployerPlusCount + revenueStats.activePlusCount || 1)
                      )} kr/mnd
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Totale betalende brukere</p>
                    <p className="text-2xl text-green-900">
                      {revenueStats.activeEmployerCount + revenueStats.activeEmployerPlusCount + revenueStats.activePlusCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Employers Tab */}
          {activeTab === 'employers' && (
            <AdminEmployerVerification accessToken={accessToken} />
          )}

          {/* Email Lists Tab */}
          {activeTab === 'emails' && (
            <AdminEmailLists accessToken={accessToken} />
          )}

          {/* Bulk Email Sender Tab */}
          {activeTab === 'bulkemail' && (
            <AdminBulkEmailSender accessToken={accessToken} />
          )}

          {/* Access Control Tab (Sub-Admins) */}
          {activeTab === 'access' && (
            <AdminAccessControl accessToken={accessToken} />
          )}

          {/* Gigs Tab - FASE 6 */}
          {activeTab === 'gigs' && (
            <AdminGigs accessToken={accessToken} />
          )}

          {/* Top Posts Tab - Mest sette prosjekter */}
          {activeTab === 'topposts' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                  <h2 className="text-2xl text-gray-800">Mest sette prosjekter</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Prosjekter sortert etter antall visninger. Klikk på lenkene for å åpne bruker eller prosjekt.
                </p>
                {loadingTopPosts ? (
                  <div className="flex justify-center py-12">
                    <Loader />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Prosjekt</th>
                          <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Visninger</th>
                          <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Bruker</th>
                          <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Lenker</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {topPosts.map((post) => (
                          <tr key={post.id} className="hover:bg-purple-50 transition-colors">
                            <td className="px-6 py-4">
                              <span className="text-gray-900 font-medium">{post.title || 'Uten tittel'}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-gray-700">{post.views.toLocaleString('nb-NO')}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-gray-600">{post.userName}</span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => navigateTo('profile', { userId: post.userId })}
                                  className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm inline-flex items-center gap-1"
                                  title="Åpne brukerprofil"
                                >
                                  <UserCheck className="w-4 h-4" />
                                  Bruker
                                </button>
                                <button
                                  onClick={() => navigateTo('profile', { userId: post.userId })}
                                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm inline-flex items-center gap-1"
                                  title="Åpne prosjekt (viser brukerprofil)"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Prosjekt
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!loadingTopPosts && topPosts.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    Ingen publiserte prosjekter funnet
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dev Logs Tab */}
          {activeTab === 'logs' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-purple-600" />
                    <h2 className="text-2xl text-gray-800">Dev Logger</h2>
                  </div>
                  <button
                    onClick={() => setShowLogViewer(true)}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <Eye className="w-5 h-5" />
                    Åpne Logger
                  </button>
                </div>
                <p className="text-gray-600">
                  Se alle dev-logger lagret i localStorage. Logger inkluderer console.log, console.error og console.warn fra frontend-koden.
                </p>
                <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Tips:</strong> Logger lagres kun i development mode og slettes automatisk etter 7 dager. Produksjon (talentfunn.no) logger ikke.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <AdminReports accessToken={accessToken} />
          )}

          {/* Terms History Tab */}
          {activeTab === 'terms' && (
            <div>
              <AdminTermsHistory accessToken={accessToken} />
            </div>
          )}

          {/* Push Notifications Tab */}
          {activeTab === 'push' && (
            <AdminPushNotifications accessToken={accessToken} />
          )}

          {/* In-app Notifications Tab */}
          {activeTab === 'inapp' && (
            <AdminInAppNotifications accessToken={accessToken} />
          )}

          {/* Announcements Tab */}
          {activeTab === 'announcements' && (
            <AdminAnnouncements accessToken={accessToken} />
          )}

          {/* Welcome Modals Tab */}
          {activeTab === 'welcome' && (
            <AdminWelcomeModals accessToken={accessToken} />
          )}

          {/* Discounts Tab */}
          {activeTab === 'discounts' && (
            <AdminDiscountCodes accessToken={accessToken} />
          )}

          {/* Inactive Accounts (Deletion) Tab */}
          {activeTab === 'deletion' && (
            <AdminInactiveAccounts accessToken={accessToken} />
          )}

          {/* Angreretter Tab */}
          {activeTab === 'angreretter' && (
            <AdminWithdrawals accessToken={accessToken} />
          )}

          {/* Health Monitoring Tab */}
          {activeTab === 'health' && (
            <AdminHealthDashboard />
          )}

          {/* ✅ NEW: Costs & Stats Tab */}
          {activeTab === 'costs' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent pb-1 font-semibold">
                  💰 Utgifter & Stats
                </h2>
              </div>

              {costStats && (
                <>
                  {/* Alerts */}
                  {costStats.alerts && costStats.alerts.length > 0 && (
                    <div className="space-y-3">
                      {costStats.alerts.map((alert: any, idx: number) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-xl border-2 ${
                            alert.type === 'warning'
                              ? 'bg-red-50 border-red-200 text-red-800'
                              : 'bg-blue-50 border-blue-200 text-blue-800'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            <span className="font-medium">{alert.message}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Revenue Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
                      <div className="flex items-center gap-3 mb-2">
                        <Crown className="w-5 h-5 text-pink-600" />
                        <span className="text-gray-600">Talentfunn Pluss</span>
                      </div>
                      <p className="text-3xl text-pink-900">{costStats.revenue.talentPluss} kr</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {costStats.users.talentPlussCount} aktive × 149 kr
                      </p>
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-blue-100">
                      <div className="flex items-center gap-3 mb-2">
                        <Users className="w-5 h-5 text-blue-600" />
                        <span className="text-gray-600">Employer Messaging</span>
                      </div>
                      <p className="text-3xl text-blue-900">{costStats.revenue.employerMessaging} kr</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {costStats.users.employerMessagingCount} aktive × 490 kr
                      </p>
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-cyan-100">
                      <div className="flex items-center gap-3 mb-2">
                        <Briefcase className="w-5 h-5 text-cyan-600" />
                        <span className="text-gray-600">Employer Pluss</span>
                      </div>
                      <p className="text-3xl text-cyan-900">{costStats.revenue.employerPluss} kr</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {costStats.users.employerPlussCount} aktive × 990 kr
                      </p>
                    </div>
                  </div>

                  {/* Costs Breakdown */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg">
                    <h3 className="text-lg font-semibold mb-4">Månedlige Kostnader</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <span className="text-gray-700">Supabase</span>
                        <span className="font-semibold">{costStats.costs.supabase} kr</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <span className="text-gray-700">Betalingsleverandor</span>
                        <span className="font-semibold">{costStats.costs.stripe} kr</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <span className="text-gray-700">Bunny CDN</span>
                        <span className="font-semibold">{costStats.costs.bunny} kr</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <span className="text-gray-700">Mailgun</span>
                        <span className="font-semibold">{costStats.costs.mailgun} kr</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <span className="text-gray-700">Netlify</span>
                        <span className="font-semibold">{costStats.costs.netlify} kr</span>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-purple-100 rounded-xl border-2 border-purple-300">
                        <span className="font-semibold text-purple-900">Total Kostnader</span>
                        <span className="text-xl font-bold text-purple-900">{costStats.totalCosts} kr</span>
                      </div>
                    </div>
                  </div>

                  {/* Profit Summary */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200">
                    <h3 className="text-lg font-semibold mb-4 text-green-900">Resultat</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Netto Profitt</p>
                        <p className={`text-3xl font-bold ${costStats.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {costStats.netProfit >= 0 ? '+' : ''}{costStats.netProfit.toFixed(2)} kr
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Profittmargin</p>
                        <p className={`text-3xl font-bold ${parseFloat(costStats.profitMargin) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {costStats.profitMargin}%
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ✅ NEW: Scenario Calculator Tab */}
          {activeTab === 'scenario' && (
            <div className="space-y-6">
              <h2 className="text-2xl bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent pb-1 font-semibold">
                📊 Leking med Tall
              </h2>
              
              <p className="text-gray-600 text-sm">
                Juster variablene under for å se hvordan ulike scenarier påvirker inntekt og vekst over 12 måneder.
              </p>

              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Vekst & Konvertering</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nye brukere per måned
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Hvor mange nye brukere registrerer seg hver måned
                    </p>
                    <input
                      type="number"
                      value={scenarioInputs.newUsersPerMonth}
                      onChange={(e) =>
                        setScenarioInputs({ ...scenarioInputs, newUsersPerMonth: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Talent konverteringsrate (%)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      % av nye talenter som oppgraderer til Talentfunn Pluss (149 kr/mnd)
                    </p>
                    <input
                      type="number"
                      value={scenarioInputs.talentConversionRate}
                      onChange={(e) =>
                        setScenarioInputs({ ...scenarioInputs, talentConversionRate: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employer konverteringsrate (%)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      % av nye arbeidsgivere som kjøper Messaging (490 kr) eller Pluss (990 kr)
                    </p>
                    <input
                      type="number"
                      value={scenarioInputs.employerConversionRate}
                      onChange={(e) =>
                        setScenarioInputs({ ...scenarioInputs, employerConversionRate: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Månedlig churn (%)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      % av betalende brukere som kansellerer abonnementet hver måned
                    </p>
                    <input
                      type="number"
                      value={scenarioInputs.monthlyChurn}
                      onChange={(e) =>
                        setScenarioInputs({ ...scenarioInputs, monthlyChurn: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Priser</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Talentfunn Pluss pris (kr/mnd)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Månedspris for Talentfunn Pluss-abonnement
                    </p>
                    <input
                      type="number"
                      value={scenarioInputs.talentPlussPrice}
                      onChange={(e) =>
                        setScenarioInputs({ ...scenarioInputs, talentPlussPrice: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employer Messaging pris (kr/mnd)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Månedspris for Arbeidsgiver Messaging-pakke
                    </p>
                    <input
                      type="number"
                      value={scenarioInputs.employerMessagingPrice}
                      onChange={(e) =>
                        setScenarioInputs({ ...scenarioInputs, employerMessagingPrice: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employer Pluss pris (kr/mnd)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Månedspris for Arbeidsgiver Pluss-pakke
                    </p>
                    <input
                      type="number"
                      value={scenarioInputs.employerPlussPrice}
                      onChange={(e) =>
                        setScenarioInputs({ ...scenarioInputs, employerPlussPrice: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Scenario Results */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-200">
                <h3 className="text-lg font-semibold mb-4 text-blue-900">12-måneders Prognose</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Totale nye brukere (12 mnd)</p>
                    <p className="text-3xl font-bold text-blue-900">
                      {scenarioInputs.newUsersPerMonth * 12}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {scenarioInputs.newUsersPerMonth} brukere/måned × 12 måneder
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Talentfunn Pluss konverteringer</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {Math.round(scenarioInputs.newUsersPerMonth * 12 * (scenarioInputs.talentConversionRate / 100))}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {scenarioInputs.talentConversionRate}% av {scenarioInputs.newUsersPerMonth * 12} brukere
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">Employer konverteringer</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {Math.round(scenarioInputs.newUsersPerMonth * 12 * (scenarioInputs.employerConversionRate / 100))}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {scenarioInputs.employerConversionRate}% av {scenarioInputs.newUsersPerMonth * 12} arbeidsgivere
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">MRR fra Talentfunn Pluss (mnd 12)</p>
                    <p className="text-3xl font-bold text-green-600">
                      {Math.round((scenarioInputs.newUsersPerMonth * (scenarioInputs.talentConversionRate / 100)) * scenarioInputs.talentPlussPrice * 12).toLocaleString()} kr
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {Math.round(scenarioInputs.newUsersPerMonth * (scenarioInputs.talentConversionRate / 100) * 12)} abonnenter × {scenarioInputs.talentPlussPrice} kr
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">MRR fra Employer Messaging (mnd 12)</p>
                    <p className="text-3xl font-bold text-green-600">
                      {Math.round((scenarioInputs.newUsersPerMonth * (scenarioInputs.employerConversionRate / 100) / 2) * scenarioInputs.employerMessagingPrice * 12).toLocaleString()} kr
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      50% av employer-konverteringer (antar 50/50 split)
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">MRR fra Employer Pluss (mnd 12)</p>
                    <p className="text-3xl font-bold text-green-600">
                      {Math.round((scenarioInputs.newUsersPerMonth * (scenarioInputs.employerConversionRate / 100) / 2) * scenarioInputs.employerPlussPrice * 12).toLocaleString()} kr
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      50% av employer-konverteringer (antar 50/50 split)
                    </p>
                  </div>

                  <div className="md:col-span-2 lg:col-span-1">
                    <p className="text-sm text-gray-600 mb-1">Total MRR (mnd 12)</p>
                    <p className="text-3xl font-bold text-green-700">
                      {Math.round(
                        ((scenarioInputs.newUsersPerMonth * (scenarioInputs.talentConversionRate / 100)) * scenarioInputs.talentPlussPrice * 12) +
                        ((scenarioInputs.newUsersPerMonth * (scenarioInputs.employerConversionRate / 100) / 2) * scenarioInputs.employerMessagingPrice * 12) +
                        ((scenarioInputs.newUsersPerMonth * (scenarioInputs.employerConversionRate / 100) / 2) * scenarioInputs.employerPlussPrice * 12)
                      ).toLocaleString()} kr
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Sum av alle inntektsstrømmer over 12 måneder
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">Churn Impact (mnd 12)</p>
                    <p className="text-3xl font-bold text-red-600">
                      -{Math.round(
                        ((scenarioInputs.newUsersPerMonth * (scenarioInputs.talentConversionRate / 100) * 12) + 
                        (scenarioInputs.newUsersPerMonth * (scenarioInputs.employerConversionRate / 100) * 12)) * (scenarioInputs.monthlyChurn / 100)
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Antall brukere tapt pga. {scenarioInputs.monthlyChurn}% månedlig churn
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">Breakeven Point</p>
                    <p className="text-3xl font-bold text-purple-700">
                      {costStats ? Math.ceil(costStats.totalCosts / ((scenarioInputs.talentPlussPrice + scenarioInputs.employerMessagingPrice + scenarioInputs.employerPlussPrice) / 3)) : '...'} brukere
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Betalende brukere nødvendig for å dekke {costStats ? costStats.totalCosts.toFixed(0) : '...'} kr i månedlige kostnader
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* User Content Modal */}
      {showUserContentModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1 font-semibold">
                Brukerens innhold
              </h3>
              <button
                onClick={() => {
                  setShowUserContentModal(false);
                  setSelectedUserId(null);
                  setUserPosts([]);
                }}
                className="p-2 hover:bg-purple-50 rounded-full transition-all"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingUserContent ? (
                <div className="flex items-center justify-center py-12">
                  <Loader size="lg" className="text-purple-600" />
                </div>
              ) : userPosts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  Ingen portfolioer funnet for denne brukeren
                </div>
              ) : (
                <div className="space-y-6">
                  {userPosts.map((post) => (
                    <div key={post.id} className="bg-gray-50 rounded-2xl p-4 space-y-3">
                      {/* Post Header */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{post.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{post.description}</p>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(post.createdAt).toLocaleDateString('nb-NO')}
                        </span>
                      </div>

                      {/* Images */}
                      {post.imageUrls && post.imageUrls.length > 0 && (
                        <div>
                          <p className="text-sm text-gray-700 font-medium mb-2">
                            <ImageIcon className="w-4 h-4 inline mr-1" />
                            Bilder ({post.imageUrls.length})
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {post.imageUrls.map((url: string, idx: number) => (
                              <a
                                key={idx}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="aspect-square rounded-lg overflow-hidden bg-gray-200 hover:opacity-80 transition-opacity"
                              >
                                <img
                                  src={url}
                                  alt={`Bilde ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Videos */}
                      {post.videoUrls && post.videoUrls.length > 0 && (
                        <div>
                          <p className="text-sm text-gray-700 font-medium mb-2">
                            <MessageSquare className="w-4 h-4 inline mr-1" />
                            Videoer ({post.videoUrls.length})
                          </p>
                          <div className="space-y-2">
                            {post.videoUrls.map((url: string, idx: number) => (
                              <a
                                key={idx}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-3 bg-white rounded-lg hover:bg-purple-50 transition-colors"
                              >
                                <MessageSquare className="w-5 h-5 text-purple-600 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900 font-medium">Video {idx + 1}</p>
                                  <p className="text-xs text-gray-500 truncate">{url}</p>
                                </div>
                                <Eye className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Categories */}
                      {post.categories && post.categories.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {post.categories.map((cat: string) => (
                            <span
                              key={cat}
                              className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
              {/* Send Notification Section */}
              {!showNotificationForm ? (
                <button
                  onClick={() => setShowNotificationForm(true)}
                  className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-all flex items-center justify-center gap-2"
                >
                  <Bell className="w-4 h-4" />
                  Send varsel til denne brukeren
                </button>
              ) : (
                <div className="bg-white rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <Bell className="w-4 h-4 text-purple-600" />
                      Send in-app varsel
                    </h4>
                    <button
                      onClick={() => {
                        setShowNotificationForm(false);
                        setNotificationTitle('');
                        setNotificationMessage('');
                        setNotificationType('admin_announcement');
                        setNotificationDestination('');
                      }}
                      className="p-1 hover:bg-gray-100 rounded-full transition-all"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>

                  {/* Notification Type */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Type varsel
                    </label>
                    <select
                      value={notificationType}
                      onChange={(e) => setNotificationType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="admin_announcement">Systemmelding</option>
                      <option value="profile_view">Profilvisning</option>
                      <option value="premium_activated">Premium aktivert</option>
                      <option value="profile_approved">Profil godkjent</option>
                      <option value="content_warning">Innholdsadvarsel</option>
                    </select>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Tittel *
                    </label>
                    <input
                      type="text"
                      value={notificationTitle}
                      onChange={(e) => setNotificationTitle(e.target.value)}
                      placeholder="F.eks: Viktig melding"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Melding *
                    </label>
                    <textarea
                      value={notificationMessage}
                      onChange={(e) => setNotificationMessage(e.target.value)}
                      rows={3}
                      placeholder="Skriv meldingen..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  {/* Destination */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Destination (valgfritt)
                    </label>
                    <select
                      value={notificationDestination}
                      onChange={(e) => setNotificationDestination(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Ingen</option>
                      <option value="home">Home</option>
                      <option value="messages">Messages</option>
                      <option value="profile">Profile</option>
                      <option value="subscription">Subscription</option>
                      <option value="gigs">Gigs</option>
                      <option value="my-gigs">My Gigs</option>
                      <option value="my-interests">My Interests</option>
                      <option value="gig-interests">Gig Interests</option>
                      <option value="favorites">Favorites</option>
                      <option value="saved-searches">Saved Searches</option>
                      <option value="settings">Settings</option>
                      <option value="faq">FAQ</option>
                      <option value="about">About</option>
                    </select>
                  </div>

                  {/* Send Button */}
                  <button
                    onClick={sendNotificationToUser}
                    disabled={sendingNotification || !notificationTitle.trim() || !notificationMessage.trim()}
                    className="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {sendingNotification ? 'Sender...' : 'Send varsel'}
                  </button>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => {
                  setShowUserContentModal(false);
                  setSelectedUserId(null);
                  setUserPosts([]);
                  setShowNotificationForm(false);
                  setNotificationTitle('');
                  setNotificationMessage('');
                  setNotificationType('admin_announcement');
                  setNotificationDestination('');
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={showSubscriptionConfirm}
        onClose={() => {
          setShowSubscriptionConfirm(false);
          setUserToToggle(null);
        }}
        onConfirm={toggleSubscription}
        title="Endre abonnement?"
        message="Er du sikker på at du vil endre abonnementet til denne brukeren?"
        confirmText="Endre"
        cancelText="Avbryt"
        variant="warning"
        loading={togglingSubscription}
        loadingText="Endrer..."
        portalZIndex={ADMIN_MODAL_Z}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={deleteUser}
        title="Slett bruker?"
        message="Er du SIKKER på at du vil slette denne brukeren? Denne handlingen kan ikke angres!"
        confirmText="Slett"
        cancelText="Avbryt"
        variant="danger"
        loading={deletingUser}
        loadingText="Sletter..."
        portalZIndex={ADMIN_MODAL_Z}
      />

      {/* ✅ NEW: Log Viewer Modal */}
      {showLogViewer && (
        <LogViewer onClose={() => setShowLogViewer(false)} />
      )}
      </AdminModalZContext.Provider>
    </div>,
    document.body
  );
}
